import { Box, Group, Stack, Text } from '@mantine/core';
import { AlertTriangle, Sparkles, TrendingUp } from 'lucide-react';
import type { LucideIcon } from 'lucide-react';
import type { ReactElement } from 'react';
import type { TopicCallout, TopicCalloutTone } from './TopicNarration';
import type { VizAccent } from '@/design/VizAccents';

const TONE_ICON: Record<TopicCalloutTone, LucideIcon> = {
    headline: Sparkles,
    opportunity: TrendingUp,
    watch: AlertTriangle,
};

/** Semantic colour per tone — icons read by meaning, not by the screen's (now-retired) accent. */
const TONE_STYLE: Record<TopicCalloutTone, { fg: string; bg: string }> = {
    headline: { fg: 'var(--bm-color-brand-accent)', bg: 'var(--bm-color-brand-soft)' },
    opportunity: { fg: 'var(--bm-color-success)', bg: 'var(--bm-color-success-soft)' },
    watch: { fg: 'var(--bm-color-caution-text)', bg: 'var(--bm-color-caution-soft)' },
};

interface CalloutListProps {
    callouts: TopicCallout[];
    /** @deprecated Callout colours are semantic (by tone) now; accent is ignored. */
    accent?: VizAccent;
}

/** Renders the derived takeaways for a topic as a stack of accented rows. */
export default function CalloutList({ callouts }: CalloutListProps): ReactElement | null {
    if (callouts.length === 0) return null;

    return (
        <Stack gap="xs">
            {callouts.map((callout, index) => {
                const Icon = TONE_ICON[callout.tone];
                const tone = TONE_STYLE[callout.tone];
                return (
                    <Group key={index} gap="sm" wrap="nowrap" align="flex-start">
                        <Box
                            style={{
                                width: 26,
                                height: 26,
                                flexShrink: 0,
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                background: tone.bg,
                                border: `1px solid ${tone.bg}`,
                                borderRadius: 'var(--bm-radius)',
                            }}
                        >
                            <Icon size={13} color={tone.fg} />
                        </Box>
                        <Text size="sm" lh={1.5} style={{ paddingTop: 3 }}>
                            {callout.text}
                        </Text>
                    </Group>
                );
            })}
        </Stack>
    );
}
